<template>
  <div v-show="!contactSupportOpen && !cookiePreferencesOpen" class="policy-announcement-overlay" @click.self="handleOverlayClick">
    <div class="policy-announcement-modal">
      <!-- Decline View -->
      <template v-if="declined">
        <div class="modal-body">
          <h3 class="decline-title">{{ $t('legal_announcement_decline_title') }}</h3>
          <p class="decline-message">{{ $t('legal_announcement_decline_message', { ecosystem: ecosystemName }) }}</p>
          <p class="decline-message">{{ $t('legal_announcement_decline_hint') }}</p>
        </div>

        <div class="modal-footer">
          <div class="modal-footer__actions">
            <ds-button
              :label="$t('legal_announcement_review_again')"
              variant="rounded"
              @click="declined = false"
              class="modal-footer__accept-btn"
            />
            <a
              href="#"
              class="decline-link"
              @click.prevent="signOut"
            >
              {{ $t('legal_announcement_sign_out') }}
            </a>
          </div>

          <div class="modal-footer__secondary">
            <a
              href="#"
              class="decline-link"
              @click.prevent="openContactSupport"
            >
              {{ $t('legal_announcement_contact_support') }}
            </a>
          </div>
        </div>
      </template>

      <!-- Announcement View -->
      <template v-else>
        <div class="modal-header">
          <span class="modal-label">{{ $t('legal_announcement_important_update') }}</span>
          <h2 class="modal-heading">{{ $t('legal_announcement_heading') }}</h2>
          <p class="modal-subtitle">{{ $t('legal_announcement_subtitle', { ecosystem: ecosystemName }) }}</p>
        </div>

        <div class="modal-body">
          <div class="message-callout">
            <h3 class="callout-title">{{ $t('legal_announcement_what_changed') }}</h3>
            <p class="callout-message">{{ announcement.message }}</p>
          </div>

          <div v-if="announcement.linked_policies && announcement.linked_policies.length > 0" class="policy-list">
            <div
              v-for="policy in announcement.linked_policies"
              :key="policy.type"
              class="policy-row"
            >
              <div>
                <div class="policy-name">{{ policyLabel(policy) }}</div>
                <div v-if="policy.active_from" class="policy-date">
                  Effective {{ formatDate(policy.active_from) }}
                </div>
              </div>
              <a
                :href="policy.url"
                :target="isExternalUrl(policy.url) ? '_blank' : undefined"
                :rel="isExternalUrl(policy.url) ? 'noopener' : undefined"
                class="policy-review-link"
              >
                {{ $t('legal_announcement_review_document') }} →
              </a>
            </div>
          </div>

          <p v-if="announcement.requires_acceptance" class="disclaimer">
            {{ $t('legal_announcement_disclaimer') }}
          </p>
        </div>

        <div class="modal-footer">
          <div class="modal-footer__actions">
            <ds-button
              v-if="announcement.requires_acceptance"
              :label="$t('legal_announcement_accept')"
              variant="rounded"
              @click="handleAccept"
              :disabled="accepting"
              class="modal-footer__accept-btn"
            />
            <ds-button
              v-else
              :label="$t('legal_announcement_close')"
              variant="rounded"
              @click="handleAccept"
              :disabled="accepting"
              class="modal-footer__accept-btn"
            />
            <a
              v-if="announcement.requires_acceptance"
              href="#"
              class="decline-link"
              @click.prevent="handleDecline"
            >
              {{ $t('legal_announcement_sign_out') }}
            </a>
          </div>

          <!--
            Accepting this announcement acknowledges the cookie policy, but it
            is not a category choice: without one the server records the safe
            default, which grants nothing optional. Offer the real choice here
            rather than letting people find out later that accepting quietly
            turned everything off.
          -->
          <div v-if="includesCookiePolicy" class="modal-footer__cookie">
            <span v-if="!hasCookieDecision">{{ $t('legal_announcement_cookie_default_hint') }}</span>
            <a
              href="#"
              class="decline-link"
              @click.prevent="openCookiePreferences"
            >
              {{ $t('legal_announcement_manage_cookies') }}
            </a>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import { acceptAnnouncement } from '@/Domain/Policy/Api/announcements'
  import { hasDecided } from '@/util/cookie-consent'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import MODAL_IDS from '@/constants/modal-ids'
  import { ACTION_TYPES as USER_ACTION_TYPES } from '@/store/modules/user.js'

  const TYPE_LABELS = {
    'terms-of-use': 'Terms of Use',
    'privacy-policy': 'Privacy Policy',
    'cookie-policy': 'Cookie Policy',
  }

  export default defineComponent({
    name: 'PolicyAnnouncementModal',
    components: { DsButton },
    props: {
      announcement: {
        type: Object,
        required: true,
      },
      preview: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['accepted'],
    data() {
      return {
        accepting: false,
        declined: false,
        cookiePreferencesOpen: false,
      }
    },
    computed: {
      ecosystemName() {
        return this.$store.getters.ecosystemDisplayName
      },
      contactSupportOpen() {
        return this.$store.state.ui.visibleModals.includes(MODAL_IDS.CONTACT_SUPPORT)
      },
      includesCookiePolicy() {
        return (this.announcement.linked_policies || [])
          .some(policy => policy.type === 'cookie-policy')
      },
      /** Reactive: `hasDecided` reads the shared consent state. */
      hasCookieDecision() {
        return hasDecided()
      },
    },
    mounted() {
      document.addEventListener('keydown', this.handleEscapeKey)
      this.$bus.on('cookie-preferences-opened', this.onCookiePreferencesOpened)
      this.$bus.on('cookie-preferences-closed', this.onCookiePreferencesClosed)
    },
    beforeUnmount() {
      document.removeEventListener('keydown', this.handleEscapeKey)
      this.$bus.off('cookie-preferences-opened', this.onCookiePreferencesOpened)
      this.$bus.off('cookie-preferences-closed', this.onCookiePreferencesClosed)
    },
    methods: {
      policyLabel(policy) {
        const typeLabel = TYPE_LABELS[policy.type] || policy.type
        return policy.version_name ? `${typeLabel} ${policy.version_name}` : typeLabel
      },
      isExternalUrl(url) {
        return url && url.startsWith('http')
      },
      formatDate(dateStr) {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      },
      handleEscapeKey(event) {
        if (event.key === 'Escape') {
          if (!this.announcement.requires_acceptance || this.preview) {
            this.handleAccept()
          }
        }
      },
      handleOverlayClick() {
        if (!this.announcement.requires_acceptance || this.preview) {
          this.handleAccept()
        }
      },
      async handleAccept() {
        if (this.preview) {
          this.$emit('accepted')
          return
        }

        this.accepting = true

        try {
          await acceptAnnouncement(this.announcement.id)
          this.$emit('accepted')
        } catch (e) {
          console.error('Failed to accept announcement', e)
        }

        this.accepting = false
      },
      handleDecline() {
        this.declined = true

        // Analytics are consent-gated, so this misses anyone who refused
        // tracking cookies — the count is a floor, not a total.
        if (!this.preview) {
          trackMatomoEvent(
            MATOMO_EVENT_CATEGORIES.LEGAL_POLICY_ANNOUNCEMENT,
            MATOMO_EVENT_ACTIONS.DECLINE,
            this.announcement.id,
          )
        }
      },
      openCookiePreferences() {
        this.$bus.emit('open-cookie-preferences')
      },
      onCookiePreferencesOpened() {
        this.cookiePreferencesOpen = true
      },
      onCookiePreferencesClosed() {
        this.cookiePreferencesOpen = false
      },
      signOut() {
        this.$store.dispatch(USER_ACTION_TYPES.LOGOUT_REQUEST)
      },
      openContactSupport() {
        this.$store.commit('UI/SET_MODAL_CONTEXT', {
          title: 'Contact support',
          subject: 'Legal policy disagreement',
        })
        this.$store.commit('UI/SHOW_MODAL', MODAL_IDS.CONTACT_SUPPORT)
      },
    },
  })
</script>

<style scoped>
  .policy-announcement-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .policy-announcement-modal {
    background: white;
    border-radius: 16px;
    padding: 36px 40px;
    width: 540px;
    max-width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  }

  /* Header */
  .modal-header {
    margin-bottom: 24px;
  }

  .modal-label {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0d7a3e;
    margin-bottom: 8px;
  }

  .modal-heading {
    font-size: 22px;
    font-weight: 700;
    color: #111;
    margin: 0 0 8px 0;
    line-height: 1.3;
  }

  .modal-subtitle {
    font-size: 15px;
    color: #555;
    margin: 0;
    line-height: 1.5;
  }

  /* Body */
  .modal-body {
    margin-bottom: 24px;
  }

  .message-callout {
    border-left: 3px solid #0d7a3e;
    padding: 16px 20px;
    background: #f8faf9;
    border-radius: 0 8px 8px 0;
    margin-bottom: 24px;
  }

  .callout-title {
    font-size: 14px;
    font-weight: 700;
    color: #111;
    margin: 0 0 6px 0;
  }

  .callout-message {
    font-size: 14px;
    line-height: 1.6;
    color: #444;
    margin: 0;
  }

  /* Policy list */
  .policy-list {
    margin-bottom: 20px;
  }

  .policy-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px solid #eee;
  }

  .policy-row:last-child {
    border-bottom: none;
  }

  .policy-name {
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }

  .policy-date {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }

  .policy-review-link {
    font-size: 13px;
    font-weight: 500;
    color: #1565c0;
    text-decoration: none;
    white-space: nowrap;
  }

  .policy-review-link:hover {
    text-decoration: underline;
  }

  /* Disclaimer */
  .disclaimer {
    font-size: 12px;
    line-height: 1.5;
    color: #888;
    margin: 0;
  }

  /* Footer */
  .modal-footer {
    padding-top: 20px;
    border-top: 1px solid #eee;
  }

  .modal-footer__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .modal-footer__accept-btn {
    flex: 1;
  }
  .modal-footer__secondary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
    font-size: 13px;
    color: #6b7280;
  }

  .modal-footer__cookie {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
    font-size: 13px;
    color: #6b7280;
  }

  .decline-link {
    color: #6b7280;
    font-size: 13px;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
  }

  .decline-link:hover {
    color: #374151;
    text-decoration: underline;
  }

  /* Decline view */
  .decline-title {
    font-size: 18px;
    font-weight: 600;
    color: #111;
    margin-bottom: 12px;
  }

  .decline-message {
    font-size: 15px;
    line-height: 1.6;
    color: #333;
    margin-bottom: 20px;
  }
</style>
